"use client";

import React from "react";
import { nakshatras } from "@ishvaram/domain-data";
import type { CustomerLanguage } from "@/lib/customer-language";
import { matchingCopy } from "../matching-copy";
import type { NaamCell } from "./naam-cell";

/**
 * The 27 x 4 naam-akshar table, rendered FROM the governed projection.
 *
 * Never hand-type these syllables. `nakshatras.ts` is generated from
 * `nakshatras.yaml`, which is the calibrated source a marriage score is derived
 * from; a second copy here is the rules-23/24 drifting-copies class, and it is
 * exactly how a classically wrong akshar reached a chooser once already.
 *
 * This is the RECOVERY path, not the front door — a seeker whose typed name has
 * no cell (joined onsets like Vrinda, Pragya) lands here. So it is collapsed by
 * default and opens one nakshatra at a time: 108 chips at once is unreadable on
 * the 6-inch screen most of this traffic arrives on.
 */
export function NaamAksharPicker({
  language,
  onPick,
}: {
  language: CustomerLanguage;
  onPick: (cell: NaamCell) => void;
}) {
  const copy = matchingCopy(language);
  return (
    <div className="mt-3 max-h-[320px] overflow-y-auto rounded-lg border border-ish-border">
      <ul className="divide-y divide-ish-line">
        {nakshatras.map((nakshatra) => (
          <li key={nakshatra.id} className="p-3">
            <p className="mb-2 text-caption font-semibold text-ish-text-muted">
              {language === "hi" ? nakshatra.name_hi : nakshatra.name_en}
            </p>
            <div className="flex flex-wrap gap-2">
              {(nakshatra.padas ?? []).map((pada) => (
                <button
                  key={pada.pada}
                  type="button"
                  onClick={() =>
                    onPick({
                      moon_nakshatra: nakshatra.id,
                      pada: pada.pada,
                      nakshatra_label: language === "hi" ? nakshatra.name_hi : nakshatra.name_en,
                      syllable: pada.syllable_hi ?? pada.syllable_en,
                    })
                  }
                  className="rounded-md border border-ish-border px-3 py-1.5 text-body text-ish-dark hover:border-ish-accent"
                >
                  <span className="font-semibold">{pada.syllable_hi ?? pada.syllable_en}</span>
                  <span className="ml-1.5 text-eyebrow text-ish-text-faint">
                    {copy.naamPada} {pada.pada}
                  </span>
                </button>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
